import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Mic, Square } from 'lucide-react'
import { getToken, getApiBase } from '../lib/utils'

interface VoiceRecorderProps {
  onTranscription: (text: string) => void
  disabled?: boolean
}

export default function VoiceRecorder({ onTranscription, disabled }: VoiceRecorderProps) {
  const [isRecording, setIsRecording] = useState(false)
  const [audioLevel, setAudioLevel] = useState(0)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const audioChunksRef = useRef<Blob[]>([])
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const animationFrameRef = useRef<number | null>(null)

  const stopVisualization = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
    setAudioLevel(0)
  }, [])

  const startVisualization = useCallback((stream: MediaStream) => {
    const audioContext = new AudioContext()
    const analyser = audioContext.createAnalyser()
    const source = audioContext.createMediaStreamSource(stream)
    
    analyser.fftSize = 256
    source.connect(analyser)
    
    audioContextRef.current = audioContext
    analyserRef.current = analyser
    
    const dataArray = new Uint8Array(analyser.frequencyBinCount)
    
    const updateLevel = () => {
      if (!analyserRef.current) return
      
      analyserRef.current.getByteFrequencyData(dataArray)
      const average = dataArray.reduce((a, b) => a + b) / dataArray.length
      const normalized = Math.min(average / 128, 1)
      
      setAudioLevel(normalized)
      animationFrameRef.current = requestAnimationFrame(updateLevel)
    }
    
    updateLevel()
  }, [])

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      })
      
      audioChunksRef.current = []
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data)
        }
      }
      
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' })
        
        stream.getTracks().forEach(track => track.stop())
        
        if (audioContextRef.current) {
          audioContextRef.current.close()
          audioContextRef.current = null
        }
        
        stopVisualization()
        
        await sendAudioForTranscription(audioBlob)
      }
      
      mediaRecorderRef.current = mediaRecorder
      mediaRecorder.start()
      setIsRecording(true)
      
      startVisualization(stream)
    } catch (error) {
      console.error('Erreur accès microphone:', error)
      alert('Impossible d\'accéder au microphone. Vérifiez les permissions.')
    }
  }, [startVisualization, stopVisualization])

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop()
      setIsRecording(false)
    }
  }, [isRecording])

  const sendAudioForTranscription = async (audioBlob: Blob) => {
    try {
      const formData = new FormData()
      formData.append('file', audioBlob, 'recording.webm')
      
      const token = getToken()
      const apiBase = getApiBase()
      
      const response = await fetch(`${apiBase}/transcribe/audio`, {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: formData
      })
      
      if (!response.ok) {
        throw new Error(`Erreur transcription: ${response.status}`)
      }
      
      const data = await response.json()
      
      if (data.text) {
        onTranscription(data.text)
      } else {
        console.warn('Aucun texte transcrit')
      }
    } catch (error) {
      console.error('Erreur transcription:', error)
      alert('Erreur lors de la transcription audio')
    }
  }

  useEffect(() => {
    return () => {
      stopVisualization()
      if (audioContextRef.current) {
        audioContextRef.current.close()
      }
    }
  }, [stopVisualization])

  return (
    <div className="relative flex items-center">
      {isRecording ? (
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 h-8">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1 bg-red-500 rounded-full transition-all duration-100"
                style={{
                  height: `${Math.max(4, audioLevel * 32 * (1 + Math.sin((Date.now() / 100) + i) * 0.3))}px`
                }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={stopRecording}
            className="group relative flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition hover:bg-red-600"
            aria-label="Arrêter l'enregistrement"
          >
            <Square className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={startRecording}
          disabled={disabled}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-200 bg-white text-emerald-600 shadow-sm transition hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed dark:border-emerald-900/60 dark:bg-zinc-800 dark:text-emerald-400 dark:hover:bg-zinc-700"
          aria-label="Enregistrer un message vocal"
        >
          <Mic className="h-5 w-5" />
        </button>
      )}
      
      {isRecording && (
        <div className="absolute -top-12 left-1/2 -translate-x-1/2 flex items-center gap-1 rounded-full bg-red-500 px-3 py-1.5 text-xs font-medium text-white shadow-lg">
          <div className="flex h-2 w-2 animate-pulse rounded-full bg-white" />
          <span>Enregistrement...</span>
        </div>
      )}
    </div>
  )
}
